// ---------------------------------------------------------------------------
// Multi-Step Form Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";
import {
  buildFormAction,
  buildHiddenFields,
  renderField,
  buildConditionalLogicScript,
  formRequiresMultipart,
  type FormField,
} from "../form-utils.js";

interface FormStep {
  title: string;
  fields: FormField[];
}

interface MultiStepFormProps {
  heading?: string;
  steps: FormStep[];
  submitText: string;
  successMessage?: string;
  showProgress?: boolean;
  consentText?: string;
}

function render(props: Record<string, unknown>, ctx: RenderContext): string {
  const p = props as unknown as MultiStepFormProps;

  const steps = Array.isArray(p.steps) ? p.steps : [];
  const totalSteps = steps.length;
  const submitText = p.submitText ?? "Complete";
  const successMessage = p.successMessage ?? "Thank you!";
  const showProgress = p.showProgress !== false;

  const headingHtml = p.heading
    ? `<h2 class="ease-form-heading">${text(p.heading)}</h2>\n  `
    : "";

  const progressHtml = showProgress
    ? `<div class="ease-form-progress" role="progressbar" aria-valuenow="1" aria-valuemin="1" aria-valuemax="${attr(String(totalSteps))}" aria-label="Form progress">
      <span class="ease-form-progress-text">Step 1 of ${text(String(totalSteps))}</span>
    </div>\n  `
    : "";

  const stepsHtml = steps
    .map((step, i) => {
      const isFirst = i === 0;
      const isLast = i === totalSteps - 1;
      const fieldsHtml = step.fields.map((f) => renderField(f)).join("\n      ");

      const consentHtml =
        isLast && p.consentText
          ? `\n      <div class="ease-field ease-field-consent">
        <label><input type="checkbox" name="_consent" required aria-required="true"> ${text(p.consentText)}</label>
      </div>`
          : "";

      const navHtml = `<div class="ease-form-nav">
        ${i > 0 ? '<button type="button" class="ease-btn ease-btn-secondary ease-form-prev">Back</button>' : ""}
        ${isLast ? `<button type="submit" class="ease-btn ease-btn-primary">${text(submitText)}</button>` : '<button type="button" class="ease-btn ease-btn-primary ease-form-next">Next</button>'}
      </div>`;

      return `    <div class="ease-form-step" data-step="${attr(String(i + 1))}" aria-hidden="${attr(String(!isFirst))}">
      <h3 class="ease-form-step-title">${text(step.title)}</h3>
      ${fieldsHtml}${consentHtml}
      ${navHtml}
    </div>`;
    })
    .join("\n");

  const hasConditions = steps.some((s) => s.fields.some((f) => f.conditions?.length));
  const conditionalScript = hasConditions ? `\n${buildConditionalLogicScript()}` : "";
  const encodingAttr = steps.some((step) => formRequiresMultipart(step.fields)) ? ' enctype="multipart/form-data"' : "";

  return `<section class="ease-section ease-multi-step-form" aria-label="${attr(p.heading ?? "Multi-step form")}">
  ${headingHtml}${progressHtml}<form method="POST" action="${attr(buildFormAction(ctx))}"${encodingAttr} novalidate data-ease-form="multi-step" data-total-steps="${attr(String(totalSteps))}" data-success-message="${attr(successMessage)}">
    ${buildHiddenFields(ctx)}
${stepsHtml}
  </form>${conditionalScript}
</section>`;
}

registerRenderer("multi-step-form", { render });
